/**
 * DQ-126 / DQ-175 (the "double click" family, D-231/P-56) — the confirm button of
 * ConfirmDialog locks at the click itself.
 *
 * `mutation.isPending` (the usual `loading` prop) turns true only on a later
 * render, so two clicks in the same moment both reached `onConfirm`. The lock is
 * synchronous. It opens again:
 *  - when the caller's `loading` went true and then back to false (the action settled
 *    and the dialog is still open, e.g. after an error the user can retry), or
 *  - when the caller never reports `loading` (a synchronous confirm that keeps the
 *    dialog open): after `fallbackMs`, longer than any double click, or
 *  - when the dialog is opened again / shows another question (`reset`).
 */
export const CONFIRM_LOCK_FALLBACK_MS = 800;

export function createConfirmLock() {
  let locked = false;
  let sawLoading = false;
  return {
    /** True for the first click only, until the lock opens again. */
    enter(loading: boolean): boolean {
      if (locked || loading) return false;
      locked = true;
      sawLoading = false;
      return true;
    },
    /** The caller's `loading` changed. Returns true when this unlocked the button. */
    loadingChanged(loading: boolean): boolean {
      if (loading) { sawLoading = true; return false; }
      if (locked && sawLoading) { locked = false; sawLoading = false; return true; }
      return false;
    },
    /** The fallback timer fired. Unlocks only when the caller never reported `loading`. */
    fallback(): boolean {
      if (locked && !sawLoading) { locked = false; return true; }
      return false;
    },
    reset(): void { locked = false; sawLoading = false; },
    get locked(): boolean { return locked; },
  };
}

export type ConfirmLock = ReturnType<typeof createConfirmLock>;
